/** Client-side token fingerprinting.
 *
 * The backend stores SHA1(secret + key) per token. To search by a pasted raw
 * token WITHOUT ever sending it over the wire, the SPA fetches the salt once
 * (authenticated endpoint) and hashes locally via WebCrypto. A pasted value
 * may already BE a fingerprint (both are 40-hex), so searches submit both
 * candidates.
 */
import * as sessionsApi from '@/api/sessions';

const HEX40 = /^[0-9a-f]{40}$/;

let cachedSecret: string | null = null;

export async function computeFingerprint(secret: string, tokenValue: string): Promise<string> {
  const data = new TextEncoder().encode(secret + tokenValue);
  const digest = await crypto.subtle.digest('SHA-1', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Map raw search input to the fingerprint candidates to query. */
export async function fingerprintCandidates(input: string): Promise<string[]> {
  const value = input.trim().toLowerCase();
  if (!HEX40.test(value)) return [];
  if (cachedSecret === null) {
    cachedSecret = (await sessionsApi.fingerprintSecret()).secret;
  }
  const computed = await computeFingerprint(cachedSecret, value);
  // The input might be a raw token (→ computed) or already a fingerprint (→ itself).
  return computed === value ? [value] : [computed, value];
}
